import { useRef, useState } from 'react';
import { suggestItems, type HouseholdState } from '@shared/merge.ts';
import { addItem } from '../store/state.ts';

interface Props {
  view: HouseholdState;
  listId: string;
}

/**
 * Champ d'ajout rapide.
 *
 * Deux exigences d'usage en magasin, une main sur le caddie :
 *   - **le focus n'est jamais perdu** après un ajout, pour enchaîner ;
 *   - **les suggestions viennent de l'historique local** de cette liste, ce qui
 *     fonctionne à l'identique sans réseau et ne coûte aucune requête.
 */
export default function ItemInput({ view, listId }: Props) {
  const [label, setLabel] = useState('');
  const [quantity, setQuantity] = useState('');
  const input = useRef<HTMLInputElement>(null);

  const suggestions = suggestItems(view, listId, label);

  const submit = (chosenLabel: string, chosenQuantity: string | null) => {
    void addItem(listId, chosenLabel, chosenQuantity);
    setLabel('');
    setQuantity('');
    // Le focus est rendu immédiatement : la saisie suivante peut commencer.
    input.current?.focus();
  };

  return (
    <div className="composer">
      <form
        className="composer__form"
        onSubmit={(event) => {
          event.preventDefault();
          submit(label, quantity || null);
        }}
      >
        <input
          ref={input}
          className="composer__label"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="Ajouter un article"
          aria-label="Ajouter un article"
          maxLength={200}
          enterKeyHint="done"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="sentences"
          // Volontairement PAS d'autoFocus : ouvrir l'application ferait
          // surgir le clavier, qui masquerait la liste et le champ lui-même.
          // Le focus est rendu après chaque ajout (cf. submit), ce qui est le
          // seul moment où l'utilisateur enchaîne réellement les saisies.
        />
        <input
          className="composer__quantity"
          value={quantity}
          onChange={(event) => setQuantity(event.target.value)}
          placeholder="Qté"
          aria-label="Quantité (optionnelle)"
          maxLength={60}
          enterKeyHint="done"
          autoComplete="off"
        />
        <button type="submit" className="composer__submit" disabled={label.trim().length === 0} aria-label="Ajouter">
          <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path fill="currentColor" d="M7.2 2.4h1.6v4.8h4.8v1.6H8.8v4.8H7.2V8.8H2.4V7.2h4.8z" />
          </svg>
        </button>
      </form>

      {suggestions.length > 0 && (
        <ul className="suggestions" aria-label="Suggestions">
          {suggestions.map((suggestion) => (
            <li key={suggestion.label}>
              <button
                type="button"
                className="suggestion"
                // onMouseDown plutôt que onClick : le blur du champ ne doit pas
                // faire disparaître la suggestion avant que le clic n'aboutisse.
                onMouseDown={(event) => {
                  event.preventDefault();
                  submit(suggestion.label, suggestion.quantity);
                }}
              >
                <span>{suggestion.label}</span>
                {suggestion.quantity && <span className="suggestion__quantity">{suggestion.quantity}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
